import { useState, useCallback } from 'react';
import * as api from '../services/api';

/**
 * Hook for managing feature flags (admin only)
 */
export function useFeatureFlags() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all feature flags
   */
  const fetchFlags = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await api.fetchFeatureFlags();
      
      setFlags(result.flags || []);

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get a specific feature flag by key
   */
  const getFlag = useCallback(async (key) => {
    try {
      setError(null);
      const result = await api.getFeatureFlag(key);
      return result.flag;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Update an existing feature flag
   */
  const updateFlag = useCallback(async (key, flagData) => {
    try {
      setError(null);
      const result = await api.updateFeatureFlag(key, flagData);
      
      // Update the flag in state
      setFlags((prev) => 
        prev.map((flag) => (flag.key === key ? result.flag : flag))
      );
      
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Toggle a feature flag's enabled status
   */
  const toggleFlag = useCallback(async (key) => {
    try {
      setError(null);
      const result = await api.toggleFeatureFlag(key);
      
      // Update the flag in state
      setFlags((prev) => 
        prev.map((flag) => (flag.key === key ? result.flag : flag))
      );
      
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  return {
    flags,
    loading,
    error,
    fetchFlags,
    getFlag,
    updateFlag,
    toggleFlag,
  };
}
